/**
 * Tools Helper Utilities
 * 
 * Provides utilities to normalize and access TOOLS configuration
 * Always normalizes to array format, converting from object if needed for backward compatibility
 */

/**
 * Normalizes TOOLS configuration to array format
 * Converts object to array if needed for backward compatibility
 * 
 * @param {Array|Object} tools - TOOLS configuration (array or object)
 * @returns {Array} - Normalized array of tools
 */
export const normalizeTools = (tools) => {
    if (!tools) return [];

    // If it's already an array, return it as is
    if (Array.isArray(tools)) {
        return tools;
    }

    // If it's an object, convert it to array
    return Object.keys(tools).map(key => ({
        id: key,
        enable: tools[key]?.enable ?? true,
        params: tools[key]?.params ?? {}
    }));
};

/**
 * Gets a specific tool by its ID
 * 
 * @param {Array|Object} tools - TOOLS configuration (array or object)
 * @param {string} toolId - Tool ID
 * @returns {Object|undefined} - Tool configuration or undefined if not found
 */
export const getTool = (tools, toolId) => {
    if (!tools || !toolId) return undefined;

    const normalized = normalizeTools(tools);
    return normalized.find(tool => tool.id === toolId);
};

/**
 * Checks if a tool is enabled
 * 
 * @param {Array|Object} tools - TOOLS configuration (array or object)
 * @param {string} toolId - Tool ID
 * @returns {boolean} - True if tool exists and is enabled, false otherwise
 */
export const isToolEnabled = (tools, toolId) => {
    const tool = getTool(tools, toolId);
    return tool?.enable === true;
};

/**
 * Gets tool parameters
 * 
 * @param {Array|Object} tools - TOOLS configuration (array or object)
 * @param {string} toolId - Tool ID
 * @returns {Object} - Tool parameters or empty object if not found
 */
export const getToolParams = (tools, toolId) => {
    const tool = getTool(tools, toolId);
    return tool?.params ?? {};
};

/**
 * Converts normalized tools array back to object format (for backward compatibility)
 * This is useful when you need object-style access temporarily
 * 
 * @param {Array|Object} tools - TOOLS configuration (array or object)
 * @returns {Object} - Object with tools indexed by id
 */
export const toolsToObject = (tools) => {
    const normalized = normalizeTools(tools);
    return normalized.reduce((acc, tool) => {
        if (tool && tool.id) {
            acc[tool.id] = {
                enable: tool.enable ?? true,
                params: tool.params ?? {}
            };
        }
        return acc;
    }, {});
};
